/* global GIT_SHA1 */
import { Router } from 'react-router-dom';
import DateFnsUtils from '@date-io/date-fns';
import { GTMProvider } from '@elgorditosalsero/react-gtm-hook';
import * as loom from '@loomhq/loom-embed';
import * as Sentry from '@sentry/react';
import { createBrowserHistory } from 'history';
import Lottie from 'lottie-react';
import Persona from 'persona';
import Pusher from 'pusher-js';
import { createRoot } from 'react-dom/client';
import { usePlaidLink } from 'react-plaid-link';
import ReactPlayer from 'react-player';
import YouTubePlayer from 'react-player/youtube';
import { onlyShowFocusOnTabs, GoogleButton, MuiPickersUtilsProvider } from 'seagull';
import { IoContextProvider } from "./contexts";
import { PusherProvider } from "./pusher";
import { slackClient } from "./utils/slack-client";
import { AppWithEventReporter } from "./App";
import { uploadFileToCloudStorage } from "./infra/utils";
import { RealIntercomClient } from "./intercom/intercomClient";
import { RealCookiesManager } from "./providers/CookiesManager";
import { MobileViewportWidthProvider } from "./providers/MobileViewportWidthProvider";
import { initStoreApi } from "./store/store-api";
import { StoreProvider } from "./store/StoreProvider";
import { actualReactStripeJs } from "./stripe/reactStripeJs";
import { actualDownloadCsv } from "./utils/csv.utils";
import { getTimezoneAbbreviation } from "./utils/date-timezone.utils";
import { logBIEvent } from "./utils/logBIEvent";
import { jsx as _jsx } from "react/jsx-runtime";
onlyShowFocusOnTabs();
const history = createBrowserHistory();
if (window.sentryEnvironment) {
  Sentry.init({
    dsn: 'https://ff94ed3a7d9149d3b85db4470ae506e1@o326813.ingest.sentry.io/1835583',
    environment: window.sentryEnvironment,
    integrations: [Sentry.reactRouterV5BrowserTracingIntegration({
      history
    })],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [/(my|demo).anchor.sh/],
    release: GIT_SHA1,
    denyUrls: [/^chrome-extension:\/\//i, /^safari-extension:\/\//i, /clarity\.js/i, /adsscriptloaderstatic/i, /\/\/hidden\//i, /\/appcues/i, /googletagmanager\.com/i, /this\.nexusWebSocket/i, /\/collectedforms\.js/i,
    // hubspot
    /\/li.lms-analytics/i,
    // 3rd party analytics
    /hotjar/i, /\/frame-/i, /google-analytics\.com\/analytics\.js/i, /translate\.googleapis\.com/i, /translate\.google\.com/i, /msDiscoverChatAvailable/i, /\/Applications\//,
    // Applications on macOS - can be Safari extensions.
    /\/persona\.js/,
    // persona.js library
    /js\.stripe\.com/,
    // stripe library
    /@stripe\/stripe-js/,
    // stripe library
    /intercom/,
    // Intercom
    /maps\.googleapis\.com/,
    // Google Maps sdk
    /\/parchment\//,
    // parchment 3rd party library (used by Quill)
    /\/quill\.js/,
    // Quill - our rich-text editor
    /hs-analytics/,
    // Hubspot analytics
    /material-ui\.js$/,
    // Material UI
    /zapier\.com/ // Zapier
    ],
    ignoreErrors: ["Failed to execute 'sendBeacon' on 'Navigator'", /Network Error/i, /NetworkError/i, /__gCrWeb/, /\[Parchment\]/i, /^AxiosError:/, 'ResizeObserver loop limit exceeded', "Failed to execute 'removeChild' on 'Node'", "Can't find variable: gmo", "Cannot read properties of undefined (reading 'domInteractive')", "Can't find variable: lastScrollEventTimestamp"],
    beforeBreadcrumb(breadcrumb, hint) {
      if (breadcrumb.category === 'ui.click' || breadcrumb.category === 'ui.input') {
        var _hint$event;
        const testid = hint === null || hint === void 0 || (_hint$event = hint.event) === null || _hint$event === void 0 || (_hint$event = _hint$event.target) === null || _hint$event === void 0 || (_hint$event = _hint$event.dataset) === null || _hint$event === void 0 ? void 0 : _hint$event.testid;
        if (testid) {
          breadcrumb.message = "data-testid=".concat(testid);
        }
      }
      return breadcrumb;
    }
  });
  if (window._sessionId) {
    Sentry.setTag('sessionId', window._sessionId);
  }
}
const gtmParams = {
  id: window._gtmId
  // id: 'GTM-WNBV55S', //staging
};
const getPusher = () => {
  const pusher = new Pusher(window.pusherAppKey, {
    cluster: 'us2'
  });
  return {
    subscribe: channelName => {
      return pusher.subscribe(channelName);
    },
    unsubscribe: channelName => {
      return pusher.unsubscribe(channelName);
    },
    channel: channelName => {
      return pusher.channel(channelName);
    }
  };
};
const getHotjar = () => ({
  identify(userId, payload) {
    window.hj && window.hj('identify', userId, payload);
  }
});
const loomParser = url => loom.oembed(url);
const makePersonaClient = _ref => {
  let {
    onError,
    onReady: _onReady,
    onComplete,
    onCancel
  } = _ref;
  return {
    open(inquiryId, sessionToken) {
      const client = new Persona.Client({
        inquiryId,
        sessionToken: sessionToken || undefined,
        onError,
        onReady: () => {
          _onReady && _onReady();
          client.open();
        },
        onComplete,
        onCancel
      });
    }
  };
};
function injectScriptOrLink(script) {
  return new Promise((resolve, reject) => {
    if (script.id) {
      const existingScript = document.getElementById(script.id);
      if (existingScript) {
        return resolve(null);
      }
    }
    script.onload = () => resolve(null);
    script.onerror = () => reject();
    document.head.appendChild(script);
  });
}
const root = createRoot(document.getElementById('main'));
root.render( /*#__PURE__*/_jsx(Router, {
  history: history,
  children: /*#__PURE__*/_jsx(IoContextProvider, {
    getNow: () => new Date(),
    usePlaidLink: usePlaidLink,
    slackClient: slackClient,
    locationReload: () => window.location.reload(),
    changeLocation: url => window.location.href = url,
    copyToClipboard: text => navigator.clipboard.writeText(text),
    intercomClient: new RealIntercomClient(),
    getHotjar: getHotjar,
    openNewTab: function openNewTab() {
      return window.open(...arguments);
    },
    personaClient: makePersonaClient,
    ReactPlayer: ReactPlayer,
    YoutubePlayer: YouTubePlayer,
    Lottie: Lottie,
    GoogleButton: GoogleButton,
    loomParser: loomParser,
    uploadFileToCloudStorage: uploadFileToCloudStorage,
    cookies: new RealCookiesManager(window._authDomain, window.localStorage),
    logBIEvent: logBIEvent,
    localStorage: window.localStorage,
    downloadCsv: actualDownloadCsv,
    reactStripe: actualReactStripeJs,
    getTimezoneAbbreviation: getTimezoneAbbreviation,
    injectScriptOrLink: injectScriptOrLink,
    children: /*#__PURE__*/_jsx(StoreProvider, {
      apiHost: window._apiHostname,
      mgmtHost: window._mgmtHostname,
      pdfHost: window._pdfHostname,
      apiGatewayHost: window._apiGatewayHostname,
      authDomain: window._authDomain,
      enableAppcues: window.enableAppcues,
      appcuesAccountId: window.appcuesAccountId,
      useAuthorizationHeader: window.useAuthorizationHeader,
      sessionTimeoutMS: window.sessionTimeoutMS,
      initStoreApi: initStoreApi,
      children: /*#__PURE__*/_jsx(MuiPickersUtilsProvider, {
        utils: DateFnsUtils,
        children: /*#__PURE__*/_jsx(GTMProvider, {
          state: gtmParams,
          children: /*#__PURE__*/_jsx(PusherProvider, {
            pusher: getPusher(),
            children: /*#__PURE__*/_jsx(MobileViewportWidthProvider, {
              children: /*#__PURE__*/_jsx(AppWithEventReporter, {})
            })
          })
        })
      })
    })
  })
}));